import { memo } from 'react';
import styled from '@emotion/styled';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { reverse } from 'lodash-es';

import CommentContentView from './CommentContentView';

const PopoverContent = styled.div`
  width: 280px;
  max-height: 360px;
  margin: -12px -16px;
  padding: 15px;
  overflow: auto;
  > * {
    margin-bottom: 10px;
  }
`;

const Between = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h5`
  margin-bottom: 0;
  font-size: 14px;
  color: var(--fett-text-secondary);
`;

const Button = styled.button`
  padding: 0;
  border: none;
  background: transparent;
  font-size: 12px;
  cursor: pointer;
  color: #1677ff;
`;

export interface CommentPopoverContentViewProps {
  comments: Conversation.Trade.Comment[];
  platform: string;
  needTranslate: boolean;
  displayTranslation: boolean;
  translations: Array<string | undefined>;
  translating: boolean;
  translationError: unknown;
  onToggleTranslation: () => void;
}

function CommentPopoverContentView(props: CommentPopoverContentViewProps) {
  const { t } = useTranslate();
  const {
    comments,
    platform,
    needTranslate,
    displayTranslation,
    translations,
    translating,
    translationError,
    onToggleTranslation,
  } = props;

  const operationNode = (() => {
    if (!needTranslate) return null;

    if (translating) {
      return (
        <Button onClick={(e) => e.preventDefault()} aria-disabled>
          <LoadingOutlined /> {t('trade.translatingMsg')}
        </Button>
      );
    }

    if (translationError) {
      return (
        <Button onClick={onToggleTranslation}>
          <ReloadOutlined /> {t('trade.翻译出错，点击重翻')}
        </Button>
      );
    }

    return (
      <Button onClick={onToggleTranslation}>
        {t(`trade.${displayTranslation ? '查看原文' : '查看翻译'}`)}
      </Button>
    );
  })();

  return (
    <PopoverContent onClick={(e) => e.stopPropagation()}>
      <Between>
        <Title>{t('trade.评价详情')}</Title>
        {operationNode}
      </Between>
      {reverse([...comments]).map((comment, idx) => {
        const isFollowComment = comments.length > 1 && idx === 0;
        return (
          <CommentContentView
            key={idx}
            className="border-0 border-b border-solid border-[var(--fett-border-default)] last:border-none last:!mb-0"
            comment={comment}
            platform={platform}
            displayTranslation={displayTranslation}
            translation={reverse([...translations])[idx]}
            isFollowComment={isFollowComment}
          />
        );
      })}
    </PopoverContent>
  );
}

export default memo(CommentPopoverContentView);
